import { makeVar } from "@apollo/client"
import type { Common } from "@beautypie/segment"
import { createHash } from "crypto"
// eslint-disable-next-line import/no-cycle
import { getProfileAddresses } from "lib/api"
// eslint-disable-next-line import/no-cycle
import { userVar } from "lib/user"
import type { DeepRequired } from "tracking"
// eslint-disable-next-line import/no-cycle
import { getNormalisedPhone, getNormalisedPostal } from "utils/gtm/utils"
import { isBrowser } from "utils/isBrowser"

type GTMTraits = {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  postCode?: string
}
type SegmentTraits = DeepRequired<
  Common,
  ["context", "traits"]
>["context"]["traits"]

export type Traits = GTMTraits & SegmentTraits
const hashValue = (value: string): string => {
  return createHash("sha256").update(value).digest("hex")
}

// JSON.parse and JSON.stringify are used to ensure UTF-8 encoding is achieved
const stripNonUTF8 = (value: string): string =>
  JSON.parse(JSON.stringify(value))

const transformToContextTraits = ({
  firstName,
  lastName,
  email,
  dateOfBirth,
  postCode,
  phoneNumber,
}: {
  email: string
  firstName: string
  lastName: string
  dateOfBirth?: string
  postCode?: string
  phoneNumber?: string
}): Traits | undefined => {
  // JSON.parse and JSON.stringify are used to ensure UTF-8 encoding is achieved
  if (isBrowser()) {
    return {
      firstName,
      lastName,
      emailHashed: hashValue(stripNonUTF8(email?.trim().toLowerCase())),
      firstNameHashed: hashValue(stripNonUTF8(firstName.toLowerCase())),
      lastNameHashed: hashValue(stripNonUTF8(lastName.toLowerCase())),
      postCode,
      phoneNumber,
      postalCodeHashed: postCode ? hashValue(postCode) : undefined,
      phoneNumberHashed: phoneNumber ? hashValue(phoneNumber) : undefined,
      dateOfBirthHashed: dateOfBirth
        ? hashValue(dateOfBirth.replace(/-/g, ""))
        : undefined,
    }
  }
  return undefined
}

const analyticsTraitsVar = makeVar<SegmentTraits & GTMTraits>(
  transformToContextTraits({
    email: "",
    postCode: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
  }) ?? {}
)

// This trait object differs from those in identify calls as the values are hashed as per
// Meta API requirements
export const parseTraits = async (): Promise<Traits | undefined> => {
  const analyticsTraits = await getAnalyticsTraits()

  const {
    postalCodeHashed,
    phoneNumberHashed,
    firstNameHashed,
    lastNameHashed,
    emailHashed,
    dateOfBirthHashed,
  } = analyticsTraits
  if (isBrowser()) {
    return {
      postalCodeHashed,
      firstNameHashed,
      lastNameHashed,
      emailHashed,
      dateOfBirthHashed,
      phoneNumberHashed,
    }
  }
  return undefined
}

type AnalyticsTraitsRequestState = "initial" | "loading" | "complete" | "error"
let analyticsTraitsRequestState: AnalyticsTraitsRequestState = "initial"
let analyticsTraitsPromise
export const getAnalyticsTraits = async (): Promise<Traits> => {
  const user = userVar()

  const { isLoggedIn, firstName, lastName, email, dateOfBirth } = user
  if (!isLoggedIn) {
    return {}
  }

  if (analyticsTraitsRequestState === "initial") {
    analyticsTraitsVar(
      transformToContextTraits({
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        email,
        dateOfBirth,
      })
    )
    analyticsTraitsRequestState = "loading"
    analyticsTraitsPromise = getProfileAddresses({
      runInBackground: true,
    })
      .then((profileAddresses) => {
        const {
          postCode,
          phone: phoneNumber,
          countryCode = "GB",
        } = (profileAddresses ?? []).find((add) => add.isDefaultBilling) ||
        (profileAddresses ?? []).find((add) => add.isDefaultShipping) ||
        profileAddresses?.[0] ||
        {}

        return transformToContextTraits({
          firstName: firstName ?? "",
          lastName: lastName ?? "",
          email,
          dateOfBirth,
          postCode: getNormalisedPostal(postCode, countryCode),
          phoneNumber: getNormalisedPhone(phoneNumber, countryCode),
        })
      })
      .then((transformedTraits) => {
        analyticsTraitsVar(transformedTraits)
        analyticsTraitsRequestState = "complete"
      })
      .catch(() => {
        analyticsTraitsRequestState = "error"
        console.warn("Error while fetching profile addresses")
      })
  }
  await analyticsTraitsPromise

  return analyticsTraitsVar()
}
